import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, X } from 'lucide-react';
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, LANGUAGE_CODES, type SupportedLanguage } from '@/i18n';
import { api } from '@/lib/api/client';

interface LanguageSwitcherProps {
  className?: string;
}

/**
 * Mobile-friendly language switcher for Scout PWA.
 * Opens a bottom sheet with all available languages.
 */
export function LanguageSwitcher({ className = '' }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const currentLanguage = (i18n.language?.split('-')[0] || 'en') as SupportedLanguage;

  const handleLanguageChange = (lang: SupportedLanguage) => {
    i18n.changeLanguage(lang);
    setIsOpen(false);
    setSaveError(null);
    // Signed-in Scouts: save the choice so emails and push arrive in it.
    if (api.getAccessToken()) {
      api.patch('/scout/profile', { language: lang }).catch((err) => {
        setSaveError(err instanceof Error ? err.message : i18n.t('errors:generic.tryAgain'));
      });
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`w-full bg-white rounded-xl p-4 shadow-sm border border-warm-border flex items-center gap-3 ${className}`}
        aria-label={t('settings.language')}
      >
        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
          <Globe className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex-1 text-start min-w-0">
          <p className="text-small font-semibold text-foreground">{t('settings.language')}</p>
          <p className="text-caption text-muted-foreground">{LANGUAGE_NAMES[currentLanguage]}</p>
        </div>
      </button>
      {saveError && (
        <p role="alert" className="mt-2 text-base text-destructive">
          {saveError}
        </p>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setIsOpen(false)}
          />

          <div className="relative w-full max-w-lg bg-white rounded-t-2xl animate-slide-up safe-area-bottom">
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
            </div>

            <div className="flex items-center justify-between px-4 pb-3 border-b border-warm-border">
              <h2 className="text-section font-semibold text-foreground">{t('settings.language')}</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 -me-2 rounded-full hover:bg-warm-faint active:bg-muted transition-colors touch-manipulation"
                aria-label={t('buttons.close')}
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            <div className="py-2 max-h-[60vh] overflow-y-auto overscroll-contain">
              {SUPPORTED_LANGUAGES.map((lang) => {
                const isSelected = currentLanguage === lang;
                return (
                  <button
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    className={`w-full flex items-center gap-4 px-4 py-4
                      hover:bg-warm-faint active:bg-muted
                      transition-colors touch-manipulation
                      ${isSelected ? 'bg-primary/5' : ''}`}
                    aria-current={isSelected ? 'true' : undefined}
                  >
                    <span
                      className={`flex items-center justify-center w-10 h-10 rounded-full text-small font-bold
                      ${isSelected ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}
                    >
                      {LANGUAGE_CODES[lang]}
                    </span>

                    <span
                      className={`flex-1 text-start text-base
                      ${isSelected ? 'text-primary font-semibold' : 'text-foreground'}`}
                    >
                      {LANGUAGE_NAMES[lang]}
                    </span>

                    {isSelected && <Check className="w-5 h-5 text-primary flex-shrink-0" />}
                  </button>
                );
              })}
            </div>

            <div className="h-6" />
          </div>
        </div>
      )}
    </>
  );
}
